import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import api from '../utils/api';
import { useOrg } from '../contexts/OrgContext';

export default function EstimatesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [toggling, setToggling] = useState(null);
  const { permissions } = useOrg();

  // styles consistent with your UI
  const rowCard =
    'flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-zinc-900/50 px-4 py-3';
  const pill =
    'text-[11px] rounded-full px-2 py-0.5 border border-white/10 text-zinc-300';
  const action =
    'rounded-lg border border-white/10 px-3 py-1.5 text-xs text-zinc-200 hover:bg-white/5 active:translate-y-px';

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await api.get('/estimates/files'); // hits our new endpoint
        setRows(res.data?.files || []);
      } catch (e) {
        setErr('Failed to load estimates.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async (row) => {
    const { data, error } = await supabase.storage.from(row.bucket || 'userfiles').download(row.object_path);
    if (error) return alert(error.message);
    const blobUrl = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = row.name || row.object_path.split('/').pop();
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const toggleShare = async (row) => {
    if (!permissions.canUpload) {
      alert('Sharing is disabled for your role.');
      return;
    }
    setToggling(row.id);
    try {
      await api.post(`/estimates/${row.id}/share`, { shared: !row.company_shared });
      // optimistic update
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, company_shared: !r.company_shared } : r));
    } catch (e) {
      alert('Could not change sharing.');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={rowCard}>
            <div className="h-3 w-44 rounded bg-zinc-800 animate-pulse" />
            <div className="h-3 w-24 rounded bg-zinc-800 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (err) return <div className="text-red-400 text-sm">{err}</div>;

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-white/10 bg-zinc-900/40 p-6 text-center text-sm text-zinc-400">
        No estimates yet. Upload one below.
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map(r => (
        <li key={r.id} className={rowCard}>
          <div className="min-w-0">
            <div className="truncate text-sm text-zinc-200">{r.name || r.object_path.split('/').pop()}</div>
            <div className="truncate text-[11px] text-zinc-500">{r.object_path}</div>
          </div>

          <div className="flex items-center gap-2">
            <span className={pill}>{r.company_shared ? 'Company' : 'Private'}</span>
            <button className={action} onClick={() => download(r)}>Download</button>
            <button className={`${action} ${!permissions.canUpload ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => toggleShare(r)}
              disabled={toggling === r.id || !permissions.canUpload}
            >
              {toggling === r.id ? 'Saving...' : (r.company_shared ? 'Unshare' : 'Share')}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
